import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { scrollToSection } from '../utils/scrollToSection';

const stats = [
  { num: '30+', label: 'Projects Delivered' },
  { num: '98%',  label: 'Client Retention' },
  { num: '10+',  label: 'Years Experience' },
];

export default function Hero() {
  const { colors } = useTheme();
  const [primaryHover,   setPrimaryHover]   = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);
  const [headingHovered, setHeadingHovered] = useState(false);
  const [hoveredStat, setHoveredStat] = useState(null);

  // "Delivering Future Technology" — first letters spell DFT, the company's
  // initials. Hovering the headline highlights just those three letters.
  const headingWords = ['DELIVERING', 'FUTURE', 'TECHNOLOGY'];

  const premiumBlue = '#6366f1';

  const s = {
    section: {
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: '100px 5vw 80px', position: 'relative', overflow: 'hidden',
    },
    content: { position: 'relative', zIndex: 1, maxWidth: '800px', minWidth: 0, animation: 'fadeIn 0.8s ease-out' },
    h1: {
      fontFamily: "'Inter', sans-serif", fontWeight: 800,
      fontSize: 'clamp(3.5rem, 9vw, 8rem)',
      lineHeight: 0.95, letterSpacing: '2px', marginBottom: '1.5rem', color: colors.text,
      cursor: 'pointer',
    },
    desc: (c) => ({
      fontSize: '1.05rem', color: c.muted, maxWidth: '500px',
      lineHeight: 1.75, marginBottom: '2.5rem', fontWeight: 300,
    }),
    actions:  { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '3rem' },
    btnPrimary: {
      padding: '0.9rem 2.2rem', fontWeight: 700,
      fontSize: '0.88rem', letterSpacing: '1px', textTransform: 'uppercase',
      textDecoration: 'none', transition: 'all 0.2s',
      border: `2px solid ${colors.accent}`, display: 'inline-block',
    },
    btnSecondary: {
      background: 'transparent',
      padding: '0.9rem 2.2rem', fontWeight: 500,
      fontSize: '0.88rem', letterSpacing: '1px', textTransform: 'uppercase',
      textDecoration: 'none', border: '1px solid', transition: 'all 0.2s',
      display: 'inline-block',
    },
    stats:    { display: 'flex', gap: '3rem', flexWrap: 'wrap' },
    statItem: {},
    statNum:  {
      fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: '2.8rem',
      color: colors.text, lineHeight: 1, letterSpacing: '2px',
    },
    statLabel: { fontSize: '0.72rem', color: colors.muted, letterSpacing: '2px', textTransform: 'uppercase' },
  };

  return (
    <section id="home" style={s.section}>
      <div style={s.content}>
        <h1
          style={s.h1}
          className="hero-title"
          onMouseEnter={() => setHeadingHovered(true)}
          onMouseLeave={() => setHeadingHovered(false)}
        >
          {headingWords.map((word, i) => (
            <span key={word}>
              <span
                style={{
                  transition: 'color 0.3s ease',
                  color: headingHovered ? premiumBlue : colors.text,
                }}
              >
                {word[0]}
              </span>
              <span style={{ color: colors.text }}>{word.slice(1)}</span>
              {i < headingWords.length - 1 && <br />}
            </span>
          ))}
        </h1>

        <p style={{
          fontSize: '1.05rem', color: colors.muted, maxWidth: '500px',
          lineHeight: 1.75, marginBottom: '2.5rem', fontWeight: 300,
        }}>
          We deliver bespoke software, intelligent AI solutions, and scalable digital products, engineered with precision to drive measurable business growth.
        </p>

        <div style={s.actions}>
          <a
            href="#contact"
            onClick={e => { e.preventDefault(); scrollToSection('contact'); }}
            style={{
              ...s.btnPrimary,
              background: primaryHover ? 'transparent' : colors.accentBg,
              color:      primaryHover ? colors.accent     : colors.accentText,
            }}
            onMouseEnter={() => setPrimaryHover(true)}
            onMouseLeave={() => setPrimaryHover(false)}
          >
            Start Your Project
          </a>
          <a
            href="#services"
            onClick={e => { e.preventDefault(); scrollToSection('services'); }}
            style={{
              ...s.btnSecondary,
              borderColor: secondaryHover ? colors.text : colors.borderHover,
              color:        secondaryHover ? colors.text : colors.text,
            }}
            onMouseEnter={() => setSecondaryHover(true)}
            onMouseLeave={() => setSecondaryHover(false)}
          >
            Explore Services
          </a>
        </div>

        <div style={s.stats} className="hero-stats">
          {stats.map(({ num, label }) => (
            <div key={label} style={s.statItem}>
              <div
                style={{
                  ...s.statNum,
                  cursor: 'pointer',
                  transition: 'color 0.3s ease',
                  color: hoveredStat === num ? premiumBlue : colors.text,
                }}
                onMouseEnter={() => setHoveredStat(num)}
                onMouseLeave={() => setHoveredStat(null)}
              >
                {num}
              </div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        /* Phones: the desktop minimum (3.5rem) makes "TECHNOLOGY" wider than
           the screen, so scale the headline with the screen width instead. */
        @media (max-width: 600px) {
          .hero-title { font-size: clamp(2.2rem, 11vw, 3.5rem) !important; letter-spacing: 1px !important; }
          .hero-stats { gap: 1.5rem 2rem !important; }
        }
      `}</style>
    </section>
  );
}
